// Single source of the logged-in navigation. The desktop tab bar and the phone
// sheet in App.js both render from this list so they can never drift apart.
//
// Shape: top-level entries are either a link ({ href, label, ... }) or a group
// ({ id, label, children: link[] }). Groups render as dropdowns on desktop and
// as section headers with nested links on phone.

export function shouldShowUpcomingLessons(user) {
  // subscriptionPlan reflects the live subscription, but is reset to 'none' when
  // a subscription is cancelled. When there's no live plan, fall back to
  // selectedPlan (the durable signup intent, which is never cleared) so a
  // cancelled platform-only user keeps the tab hidden. A live plan always wins,
  // so a genuine plan change is still respected.
  const hasLivePlan = user?.subscriptionPlan && user.subscriptionPlan !== 'none';
  const effectivePlan = hasLivePlan ? user.subscriptionPlan : user?.selectedPlan;
  const isPlatformOnlyPlan = ['platform_only', 'platform_only_family'].includes(effectivePlan);
  return !isPlatformOnlyPlan;
}

/** Flatten groups to links (order preserved) — useful for tests and tip lookups. */
export function flattenLoggedInNavLinks(items) {
  return items.flatMap((item) => (item.children ? item.children : [item]));
}

function stripShow(item) {
  const { show, ...rest } = item;
  return rest;
}

function visible(item) {
  return item.show !== false;
}

/**
 * @param {object} args
 * @param {object|null|undefined} args.user
 * @param {boolean} args.hasPendingCoreSkill
 * @param {number} args.pendingAssignments
 * @param {boolean} args.testMode
 * @param {boolean} [args.shopCanBuy] the child's Sparks newly cover a shop item (shopNudge.js)
 * @returns {Array<object>}
 */
export function buildLoggedInNavItems({ user, hasPendingCoreSkill, pendingAssignments, testMode, shopCanBuy = false }) {
  const items = [
    { href: '/tutor-upcoming-lessons', label: 'Tutor Upcoming Lessons', show: Boolean(user?.isTutor) },
    { href: '/upcoming-lessons', label: 'Upcoming Lessons', show: shouldShowUpcomingLessons(user) },
    { href: '/children-dashboard', label: "Children's Dashboard" },
    {
      id: 'parents-portal',
      label: 'Parents Portal',
      children: [
        { href: '/dashboard', label: 'Progress Dashboard' },
        { href: '/core-skills-homework', label: 'Core Skills Results' },
        { href: '/refer', label: 'Refer & Earn' },
      ],
    },
    {
      id: 'learning-portal',
      label: 'Learning Portal',
      children: [
        {
          href: '/core-skills',
          label: 'Core Skills',
          sparksTip: 'core-skills',
          ariaLabel: hasPendingCoreSkill ? 'Core Skills, new lesson ready' : 'Core Skills',
          badge: { count: hasPendingCoreSkill ? 1 : 0, testId: 'core-skills-pending-dot' },
        },
        {
          href: '/homework',
          label: 'Assignments',
          sparksTip: 'homework',
          ariaLabel: pendingAssignments > 0 ? `Assignments, ${pendingAssignments} pending` : 'Assignments',
          badge: { count: pendingAssignments, testId: 'assignments-pending-count' },
        },
        { href: '/worksheets', label: 'Quiz Studio', sparksTip: 'worksheets' },
        { href: '/lets-learn', label: 'Home Learning', sparksTip: 'lets-learn' },
      ],
    },
    // sparksTip: target for SparksCoachMark's data-sparks-tip lookup.
    {
      href: '/shop',
      label: "Addy's Shop",
      sparksTip: 'shop',
      ariaLabel: shopCanBuy ? "Addy's Shop, you can buy something new" : undefined,
      buySticker: shopCanBuy,
    },
    { href: '/leaderboard', label: 'Sparks Stars' },
    { href: '/achievements', label: 'Achievements' },
    { href: '/learning-session', label: 'Learning Session', show: testMode },
    { href: '/tuition-session', label: 'Video Test', show: testMode },
    { href: '/manage-subscription', label: 'Manage Subscription' },
  ];

  return items.filter(visible).map((item) => {
    if (!item.children) return stripShow(item);
    return {
      id: item.id,
      label: item.label,
      children: item.children.filter(visible).map(stripShow),
    };
  });
}
